import React from 'react';
import { NotificationItem } from '../../client/notificationsClient';
import { timeAgo } from '../../hooks/outreachMappers';
import ResearchNotificationText from './ResearchNotificationText';

/**
 * One notification in the bell panel: "<Supplier> replied · component · 2h
 * ago". Unread rows sit on the action-tinted wash with a cobalt dot; read
 * rows are plain. Every fact rendered is stored data — a missing supplier or
 * component name is simply omitted (honest UI), never invented.
 */
export interface NotificationRowProps {
  notification: NotificationItem;
  onSelect: (notification: NotificationItem) => void;
}

export const NotificationRow: React.FC<NotificationRowProps> = ({ notification, onSelect }) => {
  const unread = !notification.readAt;
  const when = timeAgo(notification.createdAt);
  const isResearch = notification.type === 'RESEARCH_READY' || notification.type === 'RESEARCH_PROBLEM';
  const meta = [notification.componentName, when].filter(Boolean).join(' · ');

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(notification)}
        className={`flex w-full items-start gap-s3 px-s5 py-s4 text-left hover:bg-surface-hover ${
          unread ? 'bg-action-bg' : 'bg-surface'
        }`}
      >
        <span
          aria-hidden="true"
          className={`mt-[5px] h-[6px] w-[6px] shrink-0 rounded-round ${
            unread ? 'bg-action' : 'bg-transparent'
          }`}
        />
        {isResearch ? (
          <ResearchNotificationText notification={notification} unread={unread} when={when} />
        ) : (
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body-sm text-ink-body">
            {notification.supplierName ? (
              <>
                <span className={`text-ink ${unread ? 'font-semibold' : 'font-medium'}`}>
                  {notification.supplierName}
                </span>{' '}
                replied
              </>
            ) : (
              <span className={`text-ink ${unread ? 'font-semibold' : 'font-medium'}`}>
                {notification.title}
              </span>
            )}
          </span>
          {meta && <span className="mt-s1 block truncate text-meta text-ink-muted">{meta}</span>}
        </span>
        )}
      </button>
    </li>
  );
};

export default NotificationRow;
